(() => {
  const southernCountries = new Set('AU NZ ZA AR CL UY PY BO PE BR NA BW ZW MZ MG MU RE LS SZ ZM MW TZ AO NC PF FJ WS TO PG SB VU TL ID'.split(' '));
  const defaults = { spring: '03-01', summer: '06-01', autumn: '09-01', winter: '12-01' };
  const opposite = { spring: 'autumn', summer: 'winter', autumn: 'spring', winter: 'summer' };
  function resolveHemisphere(country, override = 'auto') {
    return override === 'north' || override === 'south' ? override : southernCountries.has(String(country).toUpperCase()) ? 'south' : 'north';
  }
  function seasonForDate(date, starts = defaults, hemisphere = 'north', timeZone = 'Europe/Rome') {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, month: '2-digit', day: '2-digit' }).formatToParts(date);
    const today = Number(parts.find(p => p.type === 'month').value) * 100 + Number(parts.find(p => p.type === 'day').value);
    const schedule = Object.entries(defaults).map(([season, fallback]) => {
      const value = starts[season] || fallback;
      if (!/^\d{2}-\d{2}$/.test(value)) throw new Error('Invalid season date');
      const [month, day] = value.split('-').map(Number);
      const test = new Date(Date.UTC(2024, month - 1, day));
      if (month < 1 || month > 12 || day < 1 || test.getUTCMonth() !== month - 1 || test.getUTCDate() !== day) throw new Error('Invalid season date');
      return { season, value: month * 100 + day };
    }).sort((a, b) => a.value - b.value);
    if (new Set(schedule.map(s => s.value)).size !== 4) throw new Error('Duplicate season dates');
    let selected = schedule[schedule.length - 1].season;
    for (const entry of schedule) if (entry.value <= today) selected = entry.season;
    return hemisphere === 'south' ? opposite[selected] : selected;
  }
  function selectSeasonVariant(variants, season) {
    return variants.find(variant => String(variant.season).toLowerCase() === season) || null;
  }
  function buildCartItem({ variant, selection, profile, season, year, hemisphere }) {
    if (!variant || !variant.available) throw new Error('Unavailable box');
    if (!variant.planId) throw new Error('Missing selling plan');
    if (!selection || !selection.variantId) throw new Error('Missing selection');
    const properties = {
      'First box selected item': selection.title,
      'First box selected variant': selection.variantTitle,
      'First box selected variant ID': String(selection.variantId),
      'First box selected SKU': selection.sku || '—',
      'First box selected category': selection.category,
      'First box season': `${season} ${year}`,
      'Season hemisphere': hemisphere,
      'Height (cm)': String(profile.heightCm),
      'Weight (kg)': String(profile.weightKg),
      'Usual top size': profile.topSize,
      'Usual bottom size': profile.bottomSize,
      'Preferred fit': profile.fit
    };
    if (profile.age) properties['Age group'] = profile.age;
    if (profile.notes) properties['Fit notes'] = profile.notes;
    if (profile.styleLinks) properties['Style references'] = profile.styleLinks;
    const item = { id: String(variant.id), quantity: 1, properties };
    item.selling_plan = String(variant.planId);
    return item;
  }
  function propertySignature(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }
  function upgradeOriginal(line) {
    return { key: line.key, variantId: String(line.variant_id || line.id), properties: propertySignature(line.properties), price: line.final_price };
  }
  function matchesUpgradeOriginal(line, original) {
    return String(line.variant_id || line.id) === original.variantId && !line.selling_plan_allocation && propertySignature(line.properties) === original.properties;
  }
  function singleUpgradeLine(cart, original) {
    return cart && cart.item_count === 1 && Array.isArray(cart.items) && cart.items.length === 1 && cart.items[0].quantity === 1 && cart.items[0].key === original.key && (original.price === undefined || cart.items[0].final_price === original.price) && matchesUpgradeOriginal(cart.items[0], original) ? cart.items[0] : null;
  }
  function upgradeBox(cart, operation) {
    const matches = (cart.items || []).filter(line => line.properties && line.properties._cz_upgrade_operation === operation.id);
    if (matches.length !== 1) return null;
    const line = matches[0];
    const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    return line.quantity === 1 && String(line.variant_id || line.id) === operation.boxVariantId && String(plan && plan.id || '') === operation.planId ? line : null;
  }
  async function replaceWithBox({ operation, item, read, add, remove, save }) {
    let cart = await read();
    let addError = null;
    if (!operation.started) {
      if (!singleUpgradeLine(cart, operation.original)) throw new Error('upgrade_changed');
      operation.started = true;
      save(operation);
      try { await add({ ...item, properties: { ...item.properties, _cz_source: 'cart_upgrade', _cz_upgrade_operation: operation.id } }); }
      catch (error) { addError = error; }
      try { cart = await read(); }
      catch (_) { throw new Error('upgrade_uncertain'); }
    }
    if (!upgradeBox(cart, operation)) {
      if (addError && addError.rejected && singleUpgradeLine(cart, operation.original)) {
        save(null);
        throw addError;
      }
      throw new Error('upgrade_uncertain');
    }
    const originals = (cart.items || []).filter(line => matchesUpgradeOriginal(line, operation.original));
    if (originals.length > 1 || originals.some(line => line.quantity !== 1)) throw new Error('upgrade_cleanup');
    if (originals.length === 1) {
      try { await remove(originals[0].key); }
      catch (_) {}
    }
    try { cart = await read(); }
    catch (_) { throw new Error('upgrade_cleanup'); }
    if (!upgradeBox(cart, operation) || (cart.items || []).some(line => matchesUpgradeOriginal(line, operation.original))) throw new Error('upgrade_cleanup');
    operation.complete = true;
    save(operation);
    return cart;
  }
  window.CZSeasonalBoxUtils = { resolveHemisphere, seasonForDate, selectSeasonVariant, buildCartItem, upgradeOriginal, matchesUpgradeOriginal, singleUpgradeLine, upgradeBox, replaceWithBox };
  if (customElements.get('cz-seasonal-box')) return;
  class CZSeasonalBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-cz-config]').textContent);
      this.labels = this.config.labels;
      this.step = 0;
      this.selection = null;
      this.selectedCard = null;
      this.noneSelectedText = this.querySelector('[data-cz-selection-summary]').textContent;
      this.busy = false;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-cz-form]');
      this.hemisphere = resolveHemisphere(this.config.country);
      this.querySelector('[data-cz-hemisphere]').value = this.hemisphere;
      this.querySelector('[data-cz-hemisphere]').addEventListener('change', event => {
        if (this.busy) return;
        this.hemisphere = event.target.value;
        this.updateOffer();
      });
      this.querySelectorAll('[data-cz-open]').forEach(button => button.addEventListener('click', event => {
        if (this.busy) return;
        this.open(event.currentTarget);
      }));
      this.querySelectorAll('[data-cz-close]').forEach(button => button.addEventListener('click', () => { if (!this.busy) this.dialog.close(); }));
      this.dialog.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cz-box-dialog-open');
        this.updateSticky();
        if (this.opener) this.opener.focus();
      });
      this.querySelectorAll('[data-cz-tab]').forEach(tab => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.czTab));
        tab.addEventListener('keydown', event => this.tabKeys(event));
      });
      this.querySelectorAll('[data-cz-product]').forEach(card => {
        const data = JSON.parse(card.querySelector('[data-cz-product-data]').textContent);
        card.querySelector('[data-cz-choice]').addEventListener('change', () => this.choose(card, data));
        card.querySelector('[data-cz-variant]').addEventListener('change', () => {
          card.querySelector('[data-cz-choice]').checked = true;
          this.choose(card, data);
        });
      });
      this.querySelector('[data-cz-units]').addEventListener('change', () => this.updateUnits());
      this.querySelector('[data-cz-next]').addEventListener('click', () => this.next());
      this.querySelector('[data-cz-back]').addEventListener('click', () => this.showStep(this.step - 1));
      this.querySelectorAll('[data-cz-edit-choice]').forEach(button => button.addEventListener('click', () => this.showStep(0)));
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.step === 1) this.submit();
        else this.next();
      });
      this.activateTab('tops');
      this.updateUnits();
      this.initializeSticky();
      this.updateOffer();
      this.querySelector('[data-cz-no-js]').hidden = true;
      this.initializeUpgrade();
    }
    upgradeLabel(key) {
      const it = String(this.config.locale || document.documentElement.lang || '').toLowerCase().startsWith('it');
      const messages = {
        included_top: it ? 'La tua felpa è inclusa nel primo box.' : 'Your hoodie is included in your first box.',
        included_bottom: it ? 'I tuoi jeans sono inclusi nel primo box.' : 'Your jeans are included in your first box.',
        replaces: it ? 'Il box sostituisce il capo nel carrello.' : 'The box replaces the item in your cart.',
        changed_choice: it ? 'Il box sostituisce il capo nel carrello con la selezione qui sotto.' : 'The box replaces the item in your cart with the selection below.',
        upgrade_changed: it ? 'Il carrello è cambiato. Torna al carrello per verificare la tua offerta.' : 'Your cart has changed. Return to your cart to check your offer.',
        upgrade_unavailable: it ? 'Questo capo non è più disponibile nella selezione del box. Torna al carrello.' : 'This item is no longer available in the box selection. Return to your cart.',
        upgrade_uncertain: it ? 'Non possiamo confermare l’aggiornamento. Controlla il carrello prima di continuare; non aggiungeremo un altro box.' : 'We could not confirm the update. Check your cart before continuing; we will not add another box.',
        upgrade_cleanup: it ? 'Il box è stato aggiunto, ma la sostituzione non è confermata. Controlla il carrello prima di acquistare.' : 'Your box was added, but the replacement is not confirmed. Check your cart before checking out.',
        upgrade_storage: it ? 'Non è possibile completare la sostituzione in questo browser. Torna al carrello.' : 'This browser cannot complete the replacement safely. Return to your cart.',
        upgrade_pending: it ? 'Controllo del carrello…' : 'Checking your cart…',
        view_cart: it ? 'Vai al carrello' : 'View cart',
        replace_button: it ? 'Sostituisci con il box' : 'Replace with the box'
      };
      return messages[key] || key;
    }
    async cartRequest(path, body) {
      const response = await fetch(path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      let result;
      try { result = await response.json(); } catch (_) { throw new Error('upgrade_uncertain'); }
      if (!response.ok) {
        const error = new Error(typeof result.description === 'string' ? result.description : 'upgrade_uncertain');
        error.rejected = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429;
        throw error;
      }
      return result;
    }
    upgradeCard(variantId, category) {
      for (const card of this.querySelectorAll('[data-cz-product]')) {
        const data = JSON.parse(card.querySelector('[data-cz-product-data]').textContent);
        const select = card.querySelector('[data-cz-variant]');
        if (data.category === category && data.variants.some(variant => String(variant.id) === String(variantId)) && Array.from(select.options).some(option => option.value === String(variantId) && !option.disabled)) return { card, data, select };
      }
      return null;
    }
    saveUpgradeOperation(operation) {
      try {
        if (operation) sessionStorage.setItem(this.upgradeStorageKey, JSON.stringify(operation));
        else sessionStorage.removeItem(this.upgradeStorageKey);
        this.upgradeOperation = operation;
      } catch (_) { throw new Error('upgrade_storage'); }
    }
    upgradeNotice(message, error = false) {
      let notice = this.querySelector('[data-cz-upgrade-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.czUpgradeNotice = '';
        notice.setAttribute('role', 'status');
        const cta = this.querySelector('.cz-box__hero-copy [data-cz-open]');
        if (cta) cta.before(notice);
        else this.prepend(notice);
      }
      notice.className = error ? 'cz-box__error' : 'cz-box__small';
      notice.textContent = message;
      if (error) this.appendCartLink(notice);
    }
    appendCartLink(container) {
      const link = document.createElement('a');
      link.href = this.config.cartUrl;
      link.textContent = this.upgradeLabel('view_cart');
      container.append(document.createTextNode(' '), link);
    }
    updateUpgradeNotice() {
      if (!this.upgrade || this.upgradeBlocked) return;
      const same = this.selection && String(this.selection.variantId) === this.upgrade.original.variantId;
      const message = same ? `${this.upgradeLabel(this.upgrade.category === 'tops' ? 'included_top' : 'included_bottom')} ${this.upgradeLabel('replaces')}` : this.upgradeLabel('changed_choice');
      this.upgradeNotice(message);
      const order = this.querySelector('.cz-box__order');
      if (order) {
        let notice = order.querySelector('[data-cz-upgrade-review]');
        if (!notice) {
          notice = document.createElement('p');
          notice.dataset.czUpgradeReview = '';
          notice.className = 'cz-box__small';
          order.prepend(notice);
        }
        notice.textContent = message;
      }
      this.text('[data-cz-submit]', this.upgradeLabel('replace_button'));
    }
    async initializeUpgrade() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cz_upgrade') !== '1') return;
      this.upgradeRequested = true;
      this.upgradeLoading = true;
      this.upgradeNotice(this.upgradeLabel('upgrade_pending'));
      const key = params.get('cz_item') || '';
      const variantId = params.get('cz_variant') || '';
      const category = params.get('cz_category');
      this.upgradeStorageKey = `cz-seasonal-upgrade:${key}`;
      try {
        if (!key || key.length > 250 || !/^\d+$/.test(variantId) || !['tops', 'bottoms'].includes(category)) throw new Error('upgrade_unavailable');
        const choice = this.upgradeCard(variantId, category);
        if (!choice) throw new Error('upgrade_unavailable');
        let saved;
        try { saved = JSON.parse(sessionStorage.getItem(this.upgradeStorageKey) || 'null'); }
        catch (_) { throw new Error('upgrade_storage'); }
        const cart = await this.cartRequest(`${this.config.rootUrl}cart.js`);
        let line = cart.items && cart.items.find(item => item.key === key && String(item.variant_id || item.id) === variantId);
        const recovery = saved && saved.original && saved.original.key === key && saved.original.variantId === variantId && saved.started;
        if (recovery) {
          this.upgradeOperation = saved;
          this.upgrade = { original: saved.original, category };
        } else {
          if (!line || !singleUpgradeLine(cart, upgradeOriginal(line))) throw new Error('upgrade_changed');
          this.upgrade = { original: upgradeOriginal(line), category };
        }
        choice.select.value = variantId;
        choice.card.querySelector('[data-cz-choice]').checked = true;
        this.choose(choice.card, choice.data);
        this.activateTab(category);
        const image = line && (line.featured_image && line.featured_image.url || line.image);
        if (this.selection && image && /^https?:\/\//i.test(image)) {
          this.selection.image = image;
          this.updateSelectionImages();
        }
        this.updateUpgradeNotice();
      } catch (error) {
        this.upgradeBlocked = true;
        this.upgradeNotice(this.upgradeLabel(['upgrade_storage', 'upgrade_unavailable', 'upgrade_changed'].includes(error.message) ? error.message : 'upgrade_uncertain'), true);
      } finally {
        this.upgradeLoading = false;
      }
    }
    disconnectedCallback() {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        document.documentElement.classList.remove('cz-box-dialog-open');
      }
    }
    initializeSticky() {
      this.sticky = this.querySelector('[data-cz-sticky]');
      this.updateSticky();
    }
    updateSticky() {
      if (this.sticky) this.sticky.hidden = this.dialog.open;
    }
    displayCents(variant, recurring) {
      if (!variant) return null;
      const display = recurring ? variant.displaySubscriptionPriceCents : variant.displayPriceCents;
      if (Number.isFinite(display)) return display;
      const amount = recurring ? variant.subscriptionPriceCents : variant.priceCents;
      if (!Number.isFinite(amount)) return null;
      const rate = !this.config.taxesIncluded && !this.config.customerTaxExempt && variant.taxable !== false ? Number(this.config.vatRateBps) || 0 : 0;
      return amount + Math.round(amount * rate / 10000);
    }
    displayMoney(variant, recurring) {
      if (!variant) return '—';
      const formatted = recurring ? variant.displaySubscriptionPrice : variant.displayPrice;
      const amount = this.displayCents(variant, recurring);
      if (!Number.isFinite(amount)) return formatted || '—';
      try {
        return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency, currencyDisplay: 'symbol' }).format(amount / 100);
      } catch (_) { return formatted || '—'; }
    }
    updateSavings() {
      const variant = this.variant;
      const retail = this.config.retailMaxDisplayCents;
      const recurring = this.displayCents(variant, true);
      const eligible = Boolean(variant && variant.available && variant.planId && Number.isFinite(retail) && Number.isFinite(recurring) && retail > recurring && recurring >= 0);
      this.querySelectorAll('[data-cz-value]').forEach(node => { node.hidden = !eligible; });
      if (eligible) {
        try {
          const format = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency });
          this.text('[data-cz-saving-amount]', format.format((retail - recurring) / 100));
          this.text('[data-cz-retail-max]', format.format(retail / 100));
          this.text('[data-cz-saving-percent]', `${Math.floor((retail - recurring) / retail * 100)}%`);
        } catch (_) {
          this.querySelectorAll('[data-cz-value]').forEach(node => { node.hidden = true; });
        }
      }
      this.querySelectorAll('[data-cz-story-season]').forEach(node => {
        const active = node.dataset.czStorySeason === this.season;
        node.classList.toggle('is-current', active);
        node.querySelectorAll('[data-cz-season-badge]').forEach(badge => { badge.hidden = !active; });
      });
    }
    text(selector, value) {
      this.querySelectorAll(selector).forEach(node => { node.textContent = value; });
    }
    updateOffer() {
      try {
        this.season = this.config.seasonOverride === 'auto' ? seasonForDate(new Date(), this.config.starts, this.hemisphere, this.config.timeZone) : this.config.seasonOverride;
        this.variant = selectSeasonVariant(this.config.variants, this.season);
        this.configError = false;
      } catch (_) {
        this.variant = null;
        this.configError = true;
      }
      const variant = this.variant;
      this.text('[data-cz-season]', this.labels[this.season] || '—');
      this.text('[data-cz-sub-price]', variant && variant.planId ? this.displayMoney(variant, true) : '—');
      this.text('[data-cz-current-price]', this.displayMoney(variant, true));
      this.text('[data-cz-shipping-note]', this.labels.free_shipping || '');
      const taxNote = this.config.customerTaxExempt || variant && variant.taxable === false ? '' : this.config.taxesIncluded ? this.labels.taxes_included : this.config.vatRateBps > 0 ? this.labels.vat_included : this.labels.taxes_at_checkout;
      this.text('[data-cz-tax-note]', taxNote || '');
      this.text('[data-cz-current-mode]', this.labels.subscription_summary || this.labels.subscription);
      const unavailable = !variant || !variant.available || !variant.planId;
      this.querySelectorAll('[data-cz-open]').forEach(button => {
        button.disabled = unavailable;
      });
      this.updateSavings();
      this.updateSticky();
      this.text('[data-cz-offer-error]', this.configError ? this.labels.config_error : !variant || !variant.available ? this.labels.box_unavailable : !variant.planId ? this.labels.plan_unavailable : '');
      if (this.dialog.open && this.step === 1) this.renderReview();
    }
    open(opener) {
      if (this.upgradeLoading || this.upgradeBlocked || !this.variant || !this.variant.available || !this.variant.planId) return;
      this.opener = opener;
      this.dialog.showModal();
      document.documentElement.classList.add('cz-box-dialog-open');
      this.updateSticky();
      this.showStep(this.upgrade && this.selection ? 1 : 0);
    }
    activateTab(category, focus = false) {
      if (this.busy) return;
      this.querySelectorAll('[data-cz-tab]').forEach(tab => {
        const selected = tab.dataset.czTab === category;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      this.querySelectorAll('[data-cz-category]').forEach(panel => { panel.hidden = panel.dataset.czCategory !== category; });
    }
    tabKeys(event) {
      const tabs = Array.from(this.querySelectorAll('[data-cz-tab]'));
      let index = tabs.indexOf(event.currentTarget);
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index + tabs.length - 1) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      this.activateTab(tabs[index].dataset.czTab, true);
    }
    choose(card, data) {
      if (this.busy) return;
      this.selectedCard = card;
      this.selection = null;
      this.querySelectorAll('[data-cz-product]').forEach(item => item.classList.toggle('is-selected', item === card));
      const select = card.querySelector('[data-cz-variant]');
      if (!select.value && data.variants.length === 1 && data.variants[0].title === 'Default Title') select.value = String(data.variants[0].id);
      const variant = data.variants.find(item => String(item.id) === select.value);
      if (variant) {
        this.selection = { title: data.title, handle: data.handle, category: data.category, variantId: variant.id, variantTitle: variant.title, sku: variant.sku, image: data.image || '' };
        this.text('[data-cz-selection-summary]', this.selectionText());
      } else {
        this.text('[data-cz-selection-summary]', `${data.title} · ${this.labels.variant_required || this.labels.choose_error}`);
      }
      this.updateSelectionImages();
      this.updateUpgradeNotice();
      this.error('');
    }
    selectionText() {
      if (!this.selection) return this.noneSelectedText;
      return this.selection.title + (this.selection.variantTitle === 'Default Title' ? '' : ` — ${this.selection.variantTitle}`);
    }
    updateSelectionImages() {
      this.querySelectorAll('[data-cz-selection-image], [data-cz-review-image]').forEach(image => {
        const source = this.selection && this.selection.image;
        image.hidden = !source;
        if (source) { image.src = source; image.alt = this.selection.title; }
        else { image.removeAttribute('src'); image.alt = ''; }
      });
    }
    updateUnits() {
      const imperial = this.form.elements.units.value === 'imperial';
      this.querySelectorAll('[data-cz-metric]').forEach(node => { node.hidden = imperial; node.querySelectorAll('input').forEach(input => { input.disabled = imperial; }); });
      this.querySelectorAll('[data-cz-imperial]').forEach(node => { node.hidden = !imperial; node.querySelectorAll('input').forEach(input => { input.disabled = !imperial; }); });
    }
    validateProfile() {
      const panel = this.querySelector('[data-cz-step="1"]');
      for (const input of panel.querySelectorAll('input, select, textarea')) {
        if (input.matches('[data-cz-consent]')) continue;
        if (input.name === 'top_size' || input.name === 'bottom_size') input.value = input.value.trim();
        if (!input.disabled && !input.reportValidity()) return false;
      }
      return true;
    }
    profile() {
      const fields = this.form.elements;
      const imperial = fields.units.value === 'imperial';
      return {
        heightCm: Math.round(Number(imperial ? fields.height_in.value : fields.height_cm.value) * (imperial ? 2.54 : 1) * 10) / 10,
        weightKg: Math.round(Number(imperial ? fields.weight_lb.value : fields.weight_kg.value) * (imperial ? 0.45359237 : 1) * 10) / 10,
        topSize: fields.top_size.value.trim(), bottomSize: fields.bottom_size.value.trim(), fit: fields.fit.value,
        age: fields.age ? fields.age.value : '', notes: fields.notes ? fields.notes.value.trim() : '',
        styleLinks: fields.style_links ? fields.style_links.value.trim() : ''
      };
    }
    error(message) {
      const error = this.querySelector('[data-cz-error]');
      error.textContent = message;
      error.hidden = !message;
    }
    next() {
      if (this.busy) return;
      if (!this.selection) {
        this.error(this.selectedCard ? this.labels.variant_required || this.labels.choose_error : this.labels.choose_error);
        if (this.selectedCard) {
          const category = this.selectedCard.closest('[data-cz-category]');
          if (category) this.activateTab(category.dataset.czCategory);
          this.selectedCard.querySelector('[data-cz-variant]').focus();
        }
        return;
      }
      this.showStep(1);
    }
    showStep(step) {
      if (this.busy) return;
      this.step = Math.max(0, Math.min(step, 1));
      this.error('');
      this.querySelectorAll('[data-cz-step]').forEach(panel => { panel.hidden = Number(panel.dataset.czStep) !== this.step; });
      this.querySelectorAll('[data-cz-progress]').forEach(item => {
        if (Number(item.dataset.czProgress) === this.step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      this.querySelector('[data-cz-back]').hidden = this.step === 0;
      this.querySelector('[data-cz-next]').hidden = this.step === 1;
      this.querySelector('[data-cz-submit]').hidden = this.step !== 1;
      this.querySelectorAll('[data-cz-choice-summary]').forEach(node => { node.hidden = this.step !== 0; });
      if (this.step === 1) this.renderReview();
      const heading = this.querySelector(`[data-cz-step="${this.step}"] [data-cz-step-heading]`);
      heading.focus();
      this.dialog.scrollTop = 0;
      this.querySelectorAll('[data-cz-scroll]').forEach(node => { node.scrollTop = 0; });
    }
    renderReview() {
      if (!this.selection) return;
      this.text('[data-cz-review-item]', this.selectionText());
      this.updateSelectionImages();
      if (this.querySelector('[data-cz-review-profile]')) {
        const profile = this.profile();
        this.text('[data-cz-review-profile]', `${profile.heightCm} cm / ${profile.weightKg} kg · ${this.labels.top_size}: ${profile.topSize} · ${this.labels.bottom_size}: ${profile.bottomSize} · ${this.labels[profile.fit] || profile.fit}`);
      }
      this.querySelector('[data-cz-consent]').required = true;
      this.querySelector('[data-cz-consent]').disabled = false;
      this.updateUpgradeNotice();
    }
    async submit() {
      if (this.busy || this.upgradeLoading || this.upgradeBlocked || !this.selection || !this.validateProfile()) return;
      const consent = this.querySelector('[data-cz-consent]');
      if (!consent.reportValidity()) return;
      this.updateOffer();
      if (!this.variant || !this.variant.available || !this.variant.planId) { this.error(this.labels.box_unavailable); return; }
      const now = new Intl.DateTimeFormat('en-US', { timeZone: this.config.timeZone, year: 'numeric' }).format(new Date());
      const selection = { ...this.selection };
      const item = buildCartItem({ variant: this.variant, selection, profile: this.profile(), season: this.season, year: now, hemisphere: this.hemisphere });
      this.busy = true;
      this.error('');
      this.form.setAttribute('aria-busy', 'true');
      const controls = Array.from(this.querySelectorAll('button, input, select, textarea')).map(control => ({ control, disabled: control.disabled }));
      controls.forEach(({ control }) => { control.disabled = true; });
      this.text('[data-cz-submit]', this.labels.adding);
      try {
        if (this.upgradeRequested && !this.upgrade) throw new Error('upgrade_changed');
        if (this.upgrade && !this.upgradeCard(selection.variantId, selection.category)) throw new Error('upgrade_unavailable');
        if (!(this.upgradeOperation && this.upgradeOperation.started)) {
          const response = await fetch(`${this.config.rootUrl}products/${encodeURIComponent(selection.handle)}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error(this.labels.selection_unavailable);
          const selectedProduct = await response.json();
          const selectedVariant = Array.isArray(selectedProduct.variants) && selectedProduct.variants.find(variant => String(variant.id) === String(selection.variantId));
          if (!selectedVariant || !selectedVariant.available) {
            this.showStockError = true;
            throw new Error(this.labels.selection_unavailable);
          }
        }
        if (this.upgrade) {
          let operation = this.upgradeOperation;
          if (operation && (operation.boxVariantId !== String(item.id) || operation.planId !== String(item.selling_plan || '') || operation.selectedVariantId !== String(selection.variantId))) throw new Error('upgrade_uncertain');
          if (!operation) {
            const id = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            operation = { id, original: this.upgrade.original, boxVariantId: String(item.id), planId: String(item.selling_plan || ''), selectedVariantId: String(selection.variantId), started: false };
          }
          await replaceWithBox({
            operation, item,
            read: () => this.cartRequest(`${this.config.rootUrl}cart.js`),
            add: box => this.cartRequest(this.config.cartAddUrl, { items: [box] }),
            remove: key => this.cartRequest(`${this.config.rootUrl}cart/change.js`, { id: key, quantity: 0 }),
            save: saved => this.saveUpgradeOperation(saved)
          });
          window.location.assign(this.config.cartUrl);
          return;
        }
        const added = await fetch(this.config.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
        let result;
        try { result = await added.json(); } catch (_) { throw new Error(this.labels.add_uncertain); }
        if (!added.ok) throw new Error(typeof result.description === 'string' ? result.description : this.labels.add_error);
        window.location.assign(this.config.cartUrl);
      } catch (error) {
        if (this.showStockError) {
          this.selection = null;
          this.selectedCard = null;
          this.showStockError = false;
          this.querySelectorAll('[data-cz-choice]').forEach(input => { input.checked = false; });
          this.querySelectorAll('[data-cz-product]').forEach(card => card.classList.remove('is-selected'));
          this.text('[data-cz-selection-summary]', this.noneSelectedText);
          this.updateSelectionImages();
          this.busy = false;
          this.showStep(0);
        }
        const upgradeError = this.upgradeRequested && (error instanceof TypeError || String(error.message).startsWith('upgrade_'));
        this.error(upgradeError ? this.upgradeLabel(error instanceof TypeError ? 'upgrade_uncertain' : error.message) : error instanceof TypeError ? this.labels.add_uncertain : error.message || this.labels.add_error);
        if (upgradeError) this.appendCartLink(this.querySelector('[data-cz-error]'));
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        controls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.text('[data-cz-submit]', this.upgrade ? this.upgradeLabel('replace_button') : this.labels.add_to_cart);
      }
    }
  }
  customElements.define('cz-seasonal-box', CZSeasonalBox);
})();